<script setup lang="ts">
  import { computed, inject, ref, type Ref, onMounted, provide } from 'vue'
  import {
    type ClientCase,
    type ClientOrganisation,
    type ClientQuestionnaireSubmission,
    type QuestionnaireSection
  } from '@/types/generated'
  import { QuestionnaireSubmissionsService } from '@/types/generated'
  import { useRoute } from 'vue-router'
  import { genericErrorToast } from '@/lib/toasts'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import Questionaire from '@/components/questionaire/questionnaireFormV3'
  import completedQuestionaire from '@/components/questionaire/completedQuestionaire.vue'
  import { LoaderCircle } from 'lucide-vue-next'
  import client404 from '@/components/404/404.vue'
  import Header from '@/components/clientPortal/Header.vue'

  const route = useRoute()
  const { trackEvent } = useMetrics()
  const questionnaireShortId = route.params.uuid as string

  // Inject data from ClientPortalBase
  const caseRecord = inject<Ref<ClientCase | null>>('caseRecord')
  const org = inject<Ref<ClientOrganisation | null>>('org')
  const sidebarVisible = inject<Ref<boolean>>('sidebarVisible', ref(true))

  const loading = ref(true)
  const questionnaire = ref<ClientQuestionnaireSubmission | null>(null)

  const isExternalRoute = computed(() => {
    return route.path.includes('/external/')
  })

  const isExternalQuestionnaire = computed(() => {
    return questionnaire.value?.is_third_party_questionnaire
  })

  const shouldShowSidebar = computed(() => {
    return !isExternalRoute.value
  })

  const hasSubmission = computed(() => questionnaire.value !== null)
  const submissionIsCompleted = computed(() => questionnaire.value?.state === 'CLOSED')
  const submissionUrl = computed(() => questionnaire.value?.pdf_url || '')

  const metadata = computed(() => {
    if (!questionnaire.value) return {}

    return {
      title: questionnaire.value.title,
      totalQuestionCount: questionnaire.value.question_count,
      isPreview: false,
      isExternal: false
    }
  })

  // Inject questionnaire ref from parent to update it
  const parentQuestionnaire = inject<Ref<any | null>>('questionnaire')
  const parentActiveField = inject<Ref<string>>('activeField')

  provide('org', org)

  const updateActiveField = (activeFieldId: string) => {
    if (parentActiveField) {
      parentActiveField.value = activeFieldId
    }
  }

  onMounted(async () => {
    if (!caseRecord?.value) {
      console.error('Missing case record')
      loading.value = false
      return
    }

    try {
      // Try to find questionnaire in case data first
      let foundInCase = null
      if (caseRecord && caseRecord.value && caseRecord.value.questionnaire_submissions) {
        foundInCase = caseRecord.value.questionnaire_submissions.find(
          (submission) => submission.short_id === questionnaireShortId
        )
      }

      if (foundInCase) {
        questionnaire.value = foundInCase
        if (parentQuestionnaire) {
          parentQuestionnaire.value = foundInCase
        }
      } else {
        // If not found in case data, fetch it directly via API
        console.debug('Fetching questionnaire via API for uuid:', questionnaireShortId)
        const fetchedQuestionnaire =
          await QuestionnaireSubmissionsService.questionnaireSubmissionsRetrieve({
            uuid: questionnaireShortId
          })
        questionnaire.value = fetchedQuestionnaire
        if (parentQuestionnaire) {
          parentQuestionnaire.value = fetchedQuestionnaire
        }
        console.debug('Fetched questionnaire:', questionnaire.value)
      }

      if (org?.value?.uuid) {
        trackEvent(MetricEventType.CLIENT_QUESTIONNAIRE_EDIT, {
          organization_id: org.value.uuid
        })
      }
    } catch (error) {
      console.error('Error fetching questionnaire data:', error)
      genericErrorToast()
    } finally {
      loading.value = false
    }
  })
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <div
      v-if="loading"
      class="flex h-screen items-center justify-center"
    >
      <LoaderCircle
        :size="100"
        class="animate-spin"
      />
    </div>

    <template v-else>
      <completedQuestionaire
        v-if="submissionIsCompleted"
        :submission-url="submissionUrl"
      />

      <div
        v-else-if="hasSubmission && questionnaire"
        class="flex min-h-screen flex-col"
      >
        <Header
          :title="questionnaire?.title || 'Questionnaire'"
          :sidebar-visible="sidebarVisible"
          :should-show-sidebar="shouldShowSidebar"
          :show-back-button="!isExternalQuestionnaire"
          back-button-data-cy="ClientPortalQuestionnaireDetailViewMobileBackButton"
          title-data-cy="ClientPortalQuestionnaireDetailViewMobileTitle"
        />

        <!-- Main Content Area with top padding for fixed header - no scrolling -->
        <div class="min-h-[calc(100vh-85px)] pt-14 md:pt-[85px]">
          <Questionaire
            :rawSchema="(questionnaire.schema as QuestionnaireSection[]) || []"
            :metaData="metadata"
            @active-field-change="updateActiveField"
          />
        </div>
      </div>
      <client404 v-else />
    </template>
  </div>
</template>
